// Nuovo COMMONFORM ANT
// basato sul nuovo Form Ant https://next.ant.design/components/form/#components-form-demo-dynamic-rule
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Cascader,
  Col,
  Collapse,
  Form,
  InputNumber,
  Rate,
  Row,
  Spin,
  Switch,
  Tabs,
} from 'antd';
import { FormInstance, FormItemProps, FormProps } from 'antd/lib/form';
import CommonCard from 'commons/Layout/CommonCard';



import { compact, debounce, each, every, find, get, isArray, map, set, snakeCase } from 'lodash';
// import { Rule } from 'rc-field-form/lib/interface';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { shiftOptions } from '../../utils/helpers/select_helpers';

import CheckboxField from './antFields/CheckboxField';
import CustomSelectField from './antFields/CustomSelectField';
import DateField from './antFields/DateField';
import DateRange from './antFields/DateRange';
import InputField from './antFields/InputField';
import RadioField from './antFields/RadioInput';
import SelectField from './antFields/SelectField';
import SelectRemoteField from './antFields/SelectRemoteField';
import TimeField from './antFields/TimeField';
import UploadField from './antFields/UploadField';
import { formatSubmitPayload, isPromise } from './FormHelpers';
import { CommonFormFields } from './formsComponents/formTypes';

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 },
};
export interface CommonFormAntActions {
  form: FormInstance;
  getFormStatus: () => { values: any; isValid: boolean; errors: any; touched: any };
  resetForm: () => any;
  setFormValues: (newValues: any, append?: boolean) => any;
  setFieldValue: (field, value) => any;
  getFieldsValue: () => any;
  getFormValues: (cleaned?: boolean) => any;
  checkForm: (cleaned) => any;
  submit: (cleaned?: boolean) => any;
}

export interface CommonFormAntProps {
  name?: string;
  fields?: CommonFormFields[];
  title?: any;
  formLayout?: 'horizontal' | 'inline' | 'vertical';
  initialValues?: any;
  onSubmit?: (vals) => Promise<any | void> | void;
  onReset?: () => void;
  onFormChanged?: (values: any, changedFields: any) => any;
  debug?: boolean;
  allRequired?: boolean;
  submitText?: string;
  resetText?: string;
  isDeleteButton?: boolean;
  ref?: any;
  // non usati per ora
  hideSubmit?: boolean;
  resetOnStart?: boolean;
  cleanPayload?: boolean;
  stripEmpty?: boolean;
  autocomplete?: boolean;
  disabled?: boolean;
  tabForm?: 'line' | 'card' | '';
  className?: string;
  hideFeedbacks?: boolean;
  key?: string;
  permission?: any;
  role?: string;
  loading?: boolean;
  submitOnEnterKey?: boolean;
  disabledForm?: boolean;
}

const CommonFormAnt = forwardRef<FormProps, CommonFormAntProps>(
  (
    {
      name = 'CommonForm',
      fields = [],
      title,
      formLayout = 'vertical',
      initialValues,
      onSubmit,
      onReset,
      submitText = 'Invia',
      resetText = 'Annulla',
      isDeleteButton = false,
      onFormChanged,
      debug = false,
      hideSubmit = false,
      allRequired = false,
      cleanPayload = true,
      stripEmpty = false,
      autocomplete = false,
      disabled = false,
      tabForm = '',
      className = '',
      key = 'commForm',
      hideFeedbacks = true,
      permission = '*',
      role = undefined,
      loading = false,
      submitOnEnterKey = false,
      disabledForm = false
    },
    ref
  ) => {
    // Comandi che si possono invocare dall'esterno, quindi con useRef
    useImperativeHandle(ref, () => ({
      form,
      getFieldValue: form.getFieldValue,
      getFieldsValue: form.getFieldsValue,
      // getFormStatus: () => { values: any; isValid: boolean; errors: any; touched: any },
      resetForm: form.resetFields,
      setFormValues: (newValues, append = false) => {
        setFormValues(newValues, append);
      },
      setFieldValue: form.setFieldsValue,
      getFormValues: (cleaned = cleanPayload) =>
        form.validateFields().then((values) => {
          if (cleaned) values = formatSubmitPayload({ values, fields, stripEmpty });
          return values;
        }),
      submit: async (cleaned = cleanPayload) => SUBMIT_FORM(cleaned),
    }));

    //useKeyPress({ key: 'd', ctrl: true, onKeyPress: () => setIsDebug((isDebug) => !isDebug) });
    //const isAuthorized = useAuthorized({ permission, role });

    const [isDebug, setIsDebug] = useState(debug || undefined);
    const [form] = Form.useForm();
    const [triggerUpdate, setTriggerUpdate] = React.useState(false);
    const [localFormValues, setLocalFormValues] = React.useState<Object | undefined>(undefined);
    const [sending, setSending] = React.useState(false);

    const [activeTabKey, setActiveTabKey] = React.useState<string | undefined>(undefined);
    const debouncedSetLocalFormValues = (values) =>
      debounce(setLocalFormValues, 500, {
        leading: true,
        trailing: true,
      });

    useEffect(() => {
      if (!autocomplete) {
        // FIX HACK AUTOCOMPLETE
        setTimeout(fixAutocomplete, 2000);
      }
      if (initialValues) {
        setFormValues(initialValues);
      }
    }, [initialValues]);

    const setFormValues = (newValues, append = false) => {
      if (append) {
        newValues = { ...localFormValues, ...newValues };
      }
      form.setFieldsValue(newValues);
      setTimeout(() => setLocalFormValues(newValues), 500);
      setTimeout(forceFormUpdate, 600);
    };

    const fixAutocomplete = () => {
      document.querySelectorAll('.CommonFormContainer .ant-select-selector input').forEach((e) => {
        e.setAttribute('autocomplete', 'stophack');
      });
    };
    const LayoutFields = React.useMemo(() => {
      const layoutFields: any = { rows: [] };
      if (fields[0] && isArray(fields[0])) {
        each(fields, (row) => {
          each(row, (col: any) => {
            layoutFields.rows.push(col);
          });
        });
      } else {
        // Niente righe e colonne
        layoutFields.rows.push({ items: [...fields] });
      }
      return layoutFields;
    }, [fields, initialValues]);

    const FIELDS = React.useMemo(() => fields ?? [], [fields]);

    const forceFormUpdate = () => {
      setTriggerUpdate(!triggerUpdate);
    };

    const SUBMIT_FORM = async (cleaned = cleanPayload) => {
      setSending(true);

      return form
        .validateFields()
        .then((values) => {
          form.submit();
          if (cleaned) values = formatSubmitPayload({ values, fields, stripEmpty });
          return values;
        })
        .catch((errors) => {
          hideLoading();
          // setActiveTabKey(LayoutFields[1])
          onFinishFailed(errors);
          return errors;
        });
      // @ts-ignore
      // .finally(() => hideLoading())
    };

    const onFinish = async (values) => {
      console.log('ON FINISH', values);

      setSending(true);
      if (onSubmit) {

        if (cleanPayload) {
          values = formatSubmitPayload({ values, fields, stripEmpty });
        }

        if (isPromise(onSubmit)) {
          // @ts-ignore
          onSubmit(values).finally(() => {
            hideLoading(3000);
            console.log('FInally onSubmit');
          });
        } else {
          onSubmit(values);
          hideLoading();
        }
      } else hideLoading();
    };

    const onFinishFailed = ({ values, errorFields, outOfDate }) => {
      try {
        if (tabForm) {
          foundFirstFieldError(errorFields);
        } else {
          form.scrollToField(errorFields[0].name);
        }
      } catch (e) {
        console.warn(e);
      }
      hideLoading();
    };

    const foundFirstFieldError = (errorFields) => {
      const firstError = errorFields[0].name?.join('.');

      let errorContainer;
      const firstEl = find(LayoutFields, (row, index) =>
        find(row, (col, colKey) =>
          find(col.items, (item) => {
            const found = find(item.fields, (field) => field.name === firstError);
            if (found) {
              errorContainer = item;
              return item;
            }
          })
        )
      );
      if (errorContainer) {
        setActiveTabKey(errorContainer.name);
      }
    };

    const resetForm = () => {
      if (onReset) {
        onReset();
      }
      form.resetFields();
      hideLoading();
    };

    const hideLoading = (timeout = 1500) => {
      setTimeout(() => setSending(false), timeout);
    };

    const onValuesChange = (changedFields, values) => {

      debounce(setLocalFormValues, 200, {
        leading: true,
        trailing: true,
      })(values);
      // if (changedFields) {
      //   const propChanged = Object.keys(changedFields)[0];
      //   
      //   const _changedField: any = find(fields, (f: any) => f.name === propChanged);
      //   
      //   if (_changedField && _changedField.influence) {
      //     //forceFormUpdate();
      //   }
      // }
      if (values && onFormChanged) {
        onFormChanged(values, changedFields);
      }
    };

    const onFieldsChange = (changedField, changedFields) => {
      if (debug) console.log('onFieldsChange', { changedField, changedFields });
    };

    const externalChange = (field, value) => {

      // forceFormUpdate()
      const newValue = {
        [field.name]: value,
      };

      const values = form.getFieldsValue();
      const newValues = { ...values, [field.name]: value };
      form.setFieldsValue(newValues);
      onValuesChange(newValue, {});
    };

    const getFieldByType = (field, colKey = '') => {
      if (field?.hidden === true) return null;

      if (field.element || field.type === 'element' || field.type === 'group') {
        return renderElement(field, colKey);
      }
      return renderField(field);
    };

    const checkIsVisible = (field: any, isVisible = true, ListIndex = undefined) => {
      // Controllo se esiste un condizione
      if (field?.conditions?.visibleIf || field?.conditions?.visibleOr) {
        //debugger;
        // controllo se è di tipo :
        // visibleIf = tutte devono essere vere
        // visibleOr = almeno una deve essere vera
        const conditionType = field?.conditions?.visibleIf ? 'visibleIf' : 'visibleOr';
        // in base al tipo di condizione, uso un diverso metodo per controllare la visibilità
        const checkFunc = conditionType === 'visibleIf' ? every : find;

        let visibleConditions = field.conditions[conditionType];
        if (!isArray(visibleConditions)) visibleConditions = [field.conditions[conditionType]];

        isVisible = checkFunc(visibleConditions, (conditionRule) => {
          let _isVisible = false;

          let visibleValue;
          if (ListIndex !== undefined) {
            //debugger;

            const fieldsValue = form.getFieldsValue();
            const fieldName = conditionRule?.name?.replace('[x]', `[${ListIndex}]`);
            visibleValue = get(fieldsValue, fieldName);
          } else {
            visibleValue = form.getFieldValue(conditionRule.name.split('.'));
          }

          if (visibleValue == undefined) return false;

          const dependecyValue = visibleValue?.value || visibleValue;

          if (conditionRule.func) {
            _isVisible = conditionRule.func(visibleValue);
          } else {
            _isVisible = dependecyValue === conditionRule.value;
          }
          return _isVisible;
        });
      }
      return isVisible;
    };

    const renderField = (
      field: any,
      ListIndex: any = undefined,
      parentFieldName: any = undefined
    ) => {
      let isVisible = true;
      if (!field) return null;

      // CONDITION VISIBLE IF
      isVisible = checkIsVisible(field, isVisible, ListIndex);
      if (!isVisible) return null;

      if (field.influence) {
        field.onChange = (changed) => {
          // prendo valori attuali form
          const formValues = form.getFieldsValue();
          // li clono
          const resettedValues = {
            ...formValues,
          };
          // resetto ogni campo che viene influenzato,
          const influencedFields = field.influence.map((influencedField) => {
            // prendo il nome esatto basato anche sul listindex
            let influencedFieldName = influencedField;
            if (parentFieldName && ListIndex !== undefined)
              influencedFieldName = `${parentFieldName}[${ListIndex}].${influencedField}`;

            // setto il valore a undefined
            set(resettedValues, influencedFieldName, undefined);
            const fieldInstace = form.getFieldInstance(influencedFieldName);
            //debugger;
            // Lasciare potrebbe servire in futuro
            // return influencedFieldName?.split('.') ?? influencedFieldName;
            // return {
            //   name: influencedFieldName,
            //   touched: false,
            //   value: 9

            // };
          });
          // form.setFields(influencedFields)

          // riapplico i nuovi valori al form
          form.setFieldsValue(resettedValues);
        };
      }
      // CONDITION SERVICE IF
      if (field?.conditions?.serviceIf) {
        const { serviceIf } = field.conditions;
        const fieldsValue = form.getFieldsValue();
        let serviceIfName: any = [];
        if (!Array.isArray(serviceIf.name)) {
          serviceIfName = [serviceIf.name];
        } else {
          serviceIfName = serviceIf.name;
        }

        const itemsArray: any = [];
        serviceIfName.map((el: string) => {
          const elReplace = el.replace('[x]', `[${ListIndex}]`);
          const val = get(fieldsValue, elReplace);
          itemsArray.push(val);
        });
        const hasAllDependecies = compact(itemsArray)?.length === serviceIfName.length;

        field.disabled = !hasAllDependecies ?? false;
        if (hasAllDependecies) {
          field.service = async () => serviceIf.value(...itemsArray);
        }

        // const dependecyValue = form.getFieldValue(serviceIf.name.split('.'));
        // if (dependecyValue) {
        //   field.service = async () => serviceIf.value(dependecyValue);
        // }
      }

      // CONDITION SERVICE IF ARRAYFIELDS
      if (field?.conditions?.serviceIfArray) {
        const { serviceIfArray } = field.conditions;
        const dependecyValueArray: [] = form.getFieldValue(serviceIfArray.array)[ListIndex];

        if (dependecyValueArray) {
          if (Array.isArray(serviceIfArray.name)) {
            const itemsArray = [];
            serviceIfArray.name.map((el) => {
              itemsArray.push(dependecyValueArray?.[el]);
            });
            const hasAllDependecies = compact(itemsArray)?.length === serviceIfArray?.name?.length;

            field.disabled = !hasAllDependecies ?? false;
            field.service = async () => serviceIfArray.value(...itemsArray);
          } else {
            field.service = async () =>
              serviceIfArray.value(dependecyValueArray?.[serviceIfArray.name]);
          }
        }
      }

      if (!isVisible) return null;

      let name = field?.name?.split('.') ?? field.name;
      const label = field.label ?? field.text ?? field.placeholder ?? field.name;
      const placeholder = field.placeholder ?? field.label ?? field.name;
      let key = name;

      // se è un campo figlio di LIST modifico name e key
      if (ListIndex !== undefined) {
        key = name + ListIndex;
        // name = `${ListIndex}.${field.name}`;
        name = [ListIndex, field.name];
      }

      if (!autocomplete) {
        field.autoComplete = 'new-password';
      }

      // se necessarie porpr aggiuntive
      const extraFormItemProps: FormItemProps = { children: undefined };

      let theField = <InputField {...field} />;

      let shouldUpdate = false;
      field.debug = isDebug;
      switch (field.type) {

        case 'password':
        case 'textarea':
          theField = <InputField {...field} />;

          break;
        case 'number':
          theField = <InputNumber {...field} />;
          break;

        // select con opzioni statiche
        case 'select':
          theField = <SelectField {...field} placeholder={placeholder} />;
          break;

        // select con opzioni dinamiche remote
        case 'select_remote':
          theField = (
            <SelectRemoteField
              // defaultValue={defaultValue}
              ListIndex={ListIndex}
              {...field}
              placeholder={placeholder}
            />
          );
          break;

        case 'select_custom':
          theField = <CustomSelectField {...field} placeholder={placeholder} />;
          break;

        // Cascader
        case 'cascader':
          theField = <Cascader {...field} placeholder={placeholder} />;
          break;

        case 'date':
          theField = <DateField {...field} />;
          break;

        case 'dateRange':
          theField = <DateRange {...field} />;
          // theField = <DatePicker.RangePicker  {...field} />;
          break;

        case 'time':
          theField = <TimeField {...field} />;
          break;

        case 'radio':
          theField = <RadioField {...field} />;
          break;

        case 'checkbox':
          theField = <CheckboxField {...field} />;
          extraFormItemProps.valuePropName = 'checked';
          break;

        case 'switch':
          theField = <Switch {...field} />;
          break;

        case 'list':
          theField = renderListField(field);
          break;

        case 'rate':
          theField = <Rate {...field} />;
          break;

        case 'turn':
          field.options = shiftOptions;
          theField = <SelectField {...field} placeholder={placeholder} />;
          break;

        // case 'section':
        //   // sezione

        //   if (field?.fields?.length === 0) {
        //     theField = null;
        //   } else {
        //     theField = field.fields.map((subField) => renderField(subField));
        //   }
        //   break;

        case 'upload':
          theField = <UploadField {...field} />;
          extraFormItemProps.getValueFromEvent = (e) => {
            if (Array.isArray(e)) {
              return field?.multiple ? e : e?.[0];
            }
            return e && e.fileList;
          };
          break;

        default:
          break;
      }

      if (!theField) return null;

      const FieldComponent = (
        <Form.Item
          className={`commonFormInput ${field?.className || ''} type-${field.type}`}
          key={name}
          hasFeedback={hideFeedbacks ? false : field?.hasFeedback ?? true}
          extra={field?.extra}
          // shouldUpdate
          shouldUpdate={(prevValues, curValues) => field.conditions || shouldUpdate}
          name={name}
          label={label}
          {...extraFormItemProps}
          // dependencies={["job"]}

          rules={validationRules(field)}
        >
          {theField}
        </Form.Item>
      );

      // if (tabForm) {
      //   return FieldComponent
      // }

      return (
        <Col
          key={key}
          {...getColums(field)}
          className={`formCol ${field.type === 'section' ? 'formSection' : ''}`}
          id={`CommonForm_${name}`}
        >
          {FieldComponent}
        </Col>
      );
    };

    const validationRules = (field) => {
      window.scrollTo(0, 0);
      const rules: any[] = [
        {
          message:
            field.message ?? `${field?.label || field?.placeholder || field?.name} è obbligatorio`,
        },
      ];

      let isRequired = false;
      if (field.required) {
        isRequired = field.required;
      }
      // Required condizionale
      if (field?.conditions?.requiredIf) {
        const { requiredIf } = field.conditions;
        const requiredValue = form.getFieldValue(requiredIf.name.split('.'));
        if (requiredIf.func) {
          isRequired = requiredIf.func(requiredValue);
        } else {
          isRequired = (requiredValue?.value || requiredValue) === requiredIf.value;
        }
      }

      rules[0].required = isRequired;
      // if (field.type === "radio") {
      //   rules[0]["transform"] = value => (value ?? undefined);
      //   rules[0]["type"] = 'boolean';
      // }
      // select multipla
      if (field.mode === 'multiple') {
        rules[0].type = 'array';
      }

      return rules;
    };

    const renderElement = (field, aKey = '') => {
      const isVisible = checkIsVisible(field);
      if (!isVisible) return null;

      const style = field.style || {};
      if (field.element || field.type === 'element') {
        // RENDERIZZA CAMPO NON INPUT
        let element = <h1>{field.text}</h1>;

        switch (field.element) {
          case 'component':
            element = field.component;
            break;

          case 'function':
            const formValues = form.getFieldsValue();
            element = field.component(formValues);
            break;

          case 'html':
            element = <div dangerouslySetInnerHTML={{ __html: field.text }} />;
            break;

          case 'title':
            element = <h3>{field.text || field.label}</h3>;
            break;

          case 'spacer':
            element = <div className="formSpacer" {...field?.componentProps} />;
            break;

          default:
            element = <h3>{field.text}</h3>;
        }

        const key = `form_not_item_${aKey}_${field.name || snakeCase(field.text)}`;

        // const className = "field ant-form-item" + getColums(field, []);
        return (
          <Col {...getColums(field, [])} className="field ant-form-item" key={key} style={style}>
            {element}
          </Col>
        );
      }

      if (field.type === 'group') {
        let rendereFields = field?.fields?.map((field) => getFieldByType(field));
        const key = field?.containerProps?.id || field.name;
        const title = field?.containerProps?.title || field?.containerProps?.id || field.name;
        let container: any = CommonCard;

        if (field?.containerProps?.span || field?.span) {
          if (!field.containerProps) field.containerProps = {};
          field.containerProps.className = `${field.containerProps?.class} ${getColums(
            field.containerProps,
            [],
            true
          )}`;
        }

        if (tabForm) {
          // se è tabform imposto il contenitore a TabPane
          container = Tabs.TabPane;

          field.containerProps.key = key;
          field.containerProps.id = key;
          field.containerProps.tab = title;
          field.containerProps.forceRender = true;

          // wrappo il contenuto della tab in una Row
        }

        if (field.container === 'collapse') {
          const collapseKey = `collapse${key}`;
          field.containerProps.style = { width: '100%' };
          container = Collapse;
          rendereFields = (
            <Collapse.Panel header={title} key={collapseKey}>
              <Row key={`row${key}`} justify="start" align="top" gutter={16}>
                {rendereFields}
              </Row>
            </Collapse.Panel>
          );
        } else {
          rendereFields = (
            <Row key={`row${key}`} justify="start" align="top" gutter={16}>
              {rendereFields}
            </Row>
          );
        }

        // creo il gruppo di field nel contenitore
        return React.createElement(container, field.containerProps, rendereFields);
      }

    };

    const getColums = (currentCol, currentRow: any[] = [], asClassName = false) => {
      /*
    xs: '480px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
    xxl: '1600px',
    */
      const cols = {
        xxs: 24,
        xs: 24,
        sm: 24,
        md: currentCol?.span ?? 24,
        lg: currentCol?.span ?? 24,
        xl: currentCol?.span ?? 24,
        xxl: currentCol?.span ?? 24,
      };
      if (!currentCol?.span) {
        const { length } = currentRow;
        switch (length) {
          case 2:
            cols.lg = 12;
            break;
          case 3:
            cols.lg = 8;
            break;
          case 4:
            cols.lg = 6;
            break;

          default:
            cols.lg = 24;
        }
      }

      if (asClassName) {
        return `ant-col ant-col-xxs-${cols.xxs}  ant-col-xs-${cols.xs} ant-col-sm-${cols.sm} ant-col-md-${cols.md} ant-col-lg-${cols.lg} ant-col-xl-${cols.xl} ant-col-xxl-${cols.xxl}`;
      }
      return cols;
    };

    const renderListField = ({ name, elements }) => {
      const parentFieldName = name;
      name = name?.split('.') ?? name;
      return (
        <Form.List name={name}>
          {(listFields, { add, remove }) => (
            <div>
              {listFields.map((field, ListIndex, list) => (
                <Row key={field.key} justify="start" gutter={16} className="formList">
                  {elements.map((subField) => renderField(subField, ListIndex, parentFieldName))}

                  {listFields.length > 0 ? (
                    <MinusCircleOutlined
                      className={`dynamic-delete-button ${list.length - 1 === ListIndex ? 'last' : ListIndex
                        }`}
                      onClick={() => remove(field.name)}
                    />
                  ) : null}
                </Row>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => {
                    add();
                  }}
                  style={{ width: '60%' }}
                >
                  <PlusOutlined /> Aggiungi
                </Button>
              </Form.Item>
            </div>
          )}
        </Form.List>
      );
    };

    const renderAllFields = (row, index) => (
      <Row key={`row${index}`} justify="start" align="top" gutter={16}>
        {row.map((col, colKey) => (
          // creo Colonne
          <Col
            {...getColums(col, row)}
            // className="col"
            key={`col${index}${colKey}`}
          >
            {col.items && col.items.length > 0 ? (
              <Row key={`row${colKey}`} justify="start" align="top" gutter={16}>
                {col.items.map((field) => getFieldByType(field, index + colKey))}
              </Row>
            ) : null}
          </Col>
        ))}
      </Row>
    );
    if (!FIELDS || FIELDS.length === 0 || loading) return <Spin />;

    const formClassName = `CommonFormContainer ${disabledForm === false ? 'authorized' : 'notAuthorized'}`;

    return (
      <div className={formClassName} key={key}>
        {title}
        <Form
          autoComplete="new-password"
          //  {...formItemLayout}
          form={form}
          layout={formLayout}
          className={className}
          name={name}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          onValuesChange={onValuesChange}
          onFieldsChange={onFieldsChange}
          onKeyPress={(e) => {
            if (submitOnEnterKey && e.key === 'Enter') SUBMIT_FORM();
          }}
          initialValues={initialValues}
        >
          <input
            autoComplete="new-password"
            name="hidden"
            type="text"
            style={{ display: 'none' }}
          />
          {LayoutFields &&
            map(LayoutFields, (row, index) => {
              if (tabForm) {
                //debugger;
                return (
                  <Tabs
                    type={tabForm}
                    key={`tab${index}`}
                    activeKey={activeTabKey}
                    onChange={setActiveTabKey}
                  >
                    {row.map(
                      (col, colKey) =>
                        col?.items?.length &&
                        col.items.map((field) => getFieldByType(field, index + colKey))
                    )}
                  </Tabs>
                );
              }
              // creo colonne e righe
              return renderAllFields(row, index);
            })}

          {/* map(FIELDS, field => getFieldByType(field)) */}

          {hideSubmit ? null : (
            <Row className="buttonsRow">
              <Form.Item>
                <Button
                  className="mr-2"
                  type="primary"
                  // htmlType="submit"
                  loading={loading || sending}
                  onClick={() => SUBMIT_FORM()}
                  disabled={
                    loading || disabled || sending
                    // || !form.isFieldsTouched(true)
                    // || form.getFieldsError().filter(({ errors }) => errors.length).length > 0
                  }
                >
                  {submitText}
                </Button>
                &nbsp;
                <Button
                  htmlType="button"
                  className={isDeleteButton ? 'btn-danger' : ''}
                  onClick={resetForm}
                >
                  {resetText}
                </Button>
              </Form.Item>
            </Row>
          )}
        </Form>
      </div>
    );
  }
);
export default CommonFormAnt;
